import { z } from 'zod';
import { ROLES } from './roles';
import { FIX_BY_WINDOWS } from './fixBy';
import { templateContentSchema } from './templates';

// Enumerations shared by the database, API and UI.
export const AUDIT_MODES = ['practice', 'official'] as const;
export type AuditMode = (typeof AUDIT_MODES)[number];

export const AUDIT_STATUSES = ['in_progress', 'completed'] as const;
export type AuditStatus = (typeof AUDIT_STATUSES)[number];

export const TEMPLATE_STATUSES = ['draft', 'active', 'inactive', 'archived'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

export const TEMPLATE_VISIBILITY = ['all_stores', 'layouts', 'store_specific'] as const;
export type TemplateVisibility = (typeof TEMPLATE_VISIBILITY)[number];

export const STORE_LAYOUTS = ['standard', 'car_service', 'drive_thru', 'walk_up'] as const;
export type StoreLayout = (typeof STORE_LAYOUTS)[number];
export const STORE_LAYOUT_LABELS: Record<StoreLayout, string> = {
  standard: 'Standard',
  car_service: 'Car service (Carhop)',
  drive_thru: 'Drive-thru',
  walk_up: 'Walk-up',
};

export const ACTION_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type ActionPriority = (typeof ACTION_PRIORITIES)[number];

// open → in_progress → submitted (proof attached) → verified. Rejected proof → reopened.
export const ACTION_STATUSES = ['open', 'in_progress', 'submitted', 'verified', 'reopened'] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];
export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  submitted: 'Awaiting verification',
  verified: 'Verified',
  reopened: 'Reopened',
};
export const OPEN_ACTION_STATUSES: ActionStatus[] = ['open', 'in_progress', 'submitted', 'reopened'];

export const GOAL_METRICS = ['overall_score', 'section_score', 'max_failed_items'] as const;
export type GoalMetric = (typeof GOAL_METRICS)[number];

// ---- Request bodies ----
const email = z.string().trim().toLowerCase().pipe(z.email());
const id = z.uuid();
const date = z.iso.date();

export const loginBody = z.object({ email, password: z.string().min(1) });
export const passwordSchema = z.string().min(10, 'Use at least 10 characters.').max(200);
export const acceptInviteBody = z.object({ token: z.string().min(10), password: passwordSchema, name: z.string().trim().min(1).optional() });
export const forgotPasswordBody = z.object({ email });
export const resetPasswordBody = z.object({ token: z.string().min(10), password: passwordSchema });
export const changePasswordBody = z.object({ current_password: z.string().min(1), new_password: passwordSchema });

export const inviteUserBody = z.object({
  email,
  name: z.string().trim().min(1),
  role: z.enum(ROLES),
  store_ids: z.array(id).default([]),
});
export const updateUserBody = z.object({
  name: z.string().trim().min(1).optional(),
  role: z.enum(ROLES).optional(),
  active: z.boolean().optional(),
  store_ids: z.array(id).optional(),
  permission_overrides: z.record(z.string(), z.boolean()).optional(),
});

export const storeBody = z.object({
  store_number: z.string().trim().min(1),
  name: z.string().trim().min(1),
  location: z.string().trim().default(''),
  region: z.string().trim().default(''),
  layout: z.enum(STORE_LAYOUTS).default('standard'),
  kitchens: z.array(z.string().trim().min(1)).min(1).default(['Main']),
  notification_emails: z.array(email).default([]),
  active: z.boolean().default(true),
});
export const storePatchBody = storeBody.partial();

export const workerBody = z.object({
  name: z.string().trim().min(1),
  position: z.string().trim().default(''),
  active: z.boolean().default(true),
  user_id: id.nullable().default(null),
});

export const pointAssignmentsBody = z.object({
  template_id: id,
  assignments: z.array(z.object({ item_key: z.string().min(1), worker_id: id.nullable() })),
});

export const templateMetaBody = z.object({
  name: z.string().trim().min(1),
  description: z.string().default(''),
  audit_type: z.string().default(''),
  operational_purpose: z.string().default(''),
  status: z.enum(TEMPLATE_STATUSES).default('draft'),
  visibility: z.enum(TEMPLATE_VISIBILITY).default('all_stores'),
  layouts: z.array(z.enum(STORE_LAYOUTS)).default([]),
  store_ids: z.array(id).default([]),
  is_default: z.boolean().default(false),
});
export const createTemplateBody = templateMetaBody.extend({ content: templateContentSchema.optional() });
export const updateTemplateBody = templateMetaBody.partial().extend({ content: templateContentSchema.optional() });
export const publishTemplateBody = z.object({ change_note: z.string().trim().default('') });

export const startAuditBody = z.object({
  store_id: id,
  template_id: id,
  mode: z.enum(AUDIT_MODES),
  audit_date: date.optional(),
  scheduled_audit_id: id.optional(),
});
export const responseBody = z.object({
  result: z.enum(['pass', 'fail', 'na', 'pending']).optional(),
  temperature: z.number().nullable().optional(),
  comment: z.string().max(5000).nullable().optional(),
  fix_by_window: z.enum(FIX_BY_WINDOWS).nullable().optional(),
  // Optimistic-concurrency guard from offline clients: ignore writes older than the stored answer.
  client_updated_at: z.iso.datetime().optional(),
});
export const auditPatchBody = z.object({
  notes: z.string().max(20000).optional(),
  labor_inputs: z.record(z.string(), z.number().nullable()).optional(),
  audit_date: date.optional(),
});
export const unlockAuditBody = z.object({ reason: z.string().trim().min(3) });

export const createActionItemBody = z.object({
  audit_id: id,
  item_key: z.string().min(1),
  title: z.string().trim().min(1).optional(),
  detail: z.string().default(''),
  priority: z.enum(ACTION_PRIORITIES).default('medium'),
  assignee_user_id: id.nullable().default(null),
  assignee_worker_id: id.nullable().default(null),
  due_date: date.nullable().optional(),
});
export const updateActionItemBody = z.object({
  title: z.string().trim().min(1).optional(),
  detail: z.string().optional(),
  priority: z.enum(ACTION_PRIORITIES).optional(),
  assignee_user_id: id.nullable().optional(),
  assignee_worker_id: id.nullable().optional(),
  due_date: date.nullable().optional(),
});
export const submitActionItemBody = z.object({ note: z.string().trim().default(''), media_ids: z.array(id).min(1, 'Attach at least one photo as proof.') });
export const verifyActionItemBody = z.object({ note: z.string().trim().default('') });
export const rejectActionItemBody = z.object({ reason: z.string().trim().min(3) });
export const commentActionItemBody = z.object({ note: z.string().trim().min(1) });

export const goalBody = z.object({
  metric: z.enum(GOAL_METRICS),
  section_key: z.string().nullable().default(null),
  target: z.number(),
  label: z.string().trim().default(''),
  active: z.boolean().default(true),
});

export const scheduledAuditBody = z.object({
  store_id: id,
  template_id: id.nullable().default(null),
  auditor_user_id: id.nullable().default(null),
  scheduled_for: date,
  scheduled_time: z.string().regex(/^\d{2}:\d{2}$/).nullable().default(null),
  notes: z.string().default(''),
});
export const scheduledAuditPatchBody = scheduledAuditBody.partial().extend({
  status: z.enum(['scheduled', 'done', 'cancelled']).optional(),
});
